import Vue, { computed, unref } from 'vue'
import type { Ref } from 'vue'
import { Message } from 'element-ui'

// X のポスト本文。URL は別パラメータで渡すのでここには入れない。
const SHARE_TEXT = '無料で簡単に履歴書が作れる「yagish（ヤギッシュ）」'
// window が無い環境（SSR / テスト）向けのフォールバック
const DEFAULT_SHARE_ORIGIN = 'https://www.yagish.jp'

export type ShareChannel = 'copy' | 'line' | 'x' | 'web_share'
export type WebShareResult = 'shared' | 'cancelled' | 'unsupported'

/**
 * シェア先で流入元を GA4 で切り分けられるように、チャネルごとの utm を付けた
 * トップページ URL を作る。
 */
const buildShareUrl = (channel: ShareChannel, placement: string): string => {
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : DEFAULT_SHARE_ORIGIN
  return `${origin}/?utm_source=${channel}&utm_medium=referral&utm_campaign=${placement}`
}

/** Clipboard API が使えない環境（古い iOS Safari / 非 https）向け */
const fallbackCopy = (text: string): boolean => {
  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', 'readonly')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    return true
  } catch (e) {
    console.error(e)
    return false
  }
}

const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (e) {
      console.error(e)
    }
  }
  return fallbackCopy(text)
}

type UseShareInviteOptions = {
  /** どの画面のシェアかを計測で区別する識別子 */
  placement: string | Ref<string>
  formatId?: string | Ref<string>
}

export const useShareInvite = (options: UseShareInviteOptions) => {
  const placement = computed(() => unref(options.placement))
  const formatId = computed(() => unref(options.formatId) || '')

  const isMobileDevice = computed(() => {
    const deviceLib = (Vue.prototype as any).$deviceLib
    return !!(deviceLib && deviceLib.isMobileDevice)
  })

  // SP の共有ボタン（OS のシェアシート）が使えるか
  const canWebShare = computed(
    () =>
      typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  )

  const shareUrlFor = (channel: ShareChannel) =>
    buildShareUrl(channel, placement.value)

  const lineShareUrl = computed(
    () =>
      'https://social-plugins.line.me/lineit/share?url=' +
      encodeURIComponent(shareUrlFor('line'))
  )
  const xShareUrl = computed(
    () =>
      'https://x.com/intent/post?text=' +
      encodeURIComponent(SHARE_TEXT) +
      '&url=' +
      encodeURIComponent(shareUrlFor('x'))
  )

  const sendShareEvent = (
    action: string,
    params: Record<string, unknown> = {}
  ) => {
    const gtm = (Vue.prototype as any).$gtm
    if (!gtm || !gtm.sendShareEvent) return
    gtm.sendShareEvent(action, {
      share_placement: placement.value,
      format_id: formatId.value,
      ...params
    })
  }

  /** PC の「URLコピー」。成功時に true を返す（トースト表示までやる） */
  const copyShareUrl = async (): Promise<boolean> => {
    const copied = await copyTextToClipboard(shareUrlFor('copy'))
    if (copied) {
      Message({ message: 'URLをコピーしました。', type: 'success' })
      sendShareEvent('click', { share_channel: 'copy' })
    } else {
      Message({ message: 'URLのコピーに失敗しました。', type: 'warning' })
    }
    return copied
  }

  /**
   * SP の「共有」。OS のシェアシートを開く。
   * 未対応端末では URL コピーにフォールバックする。
   */
  const shareViaWebShare = async (): Promise<WebShareResult> => {
    if (!canWebShare.value) return 'unsupported'
    try {
      await navigator.share({
        title: SHARE_TEXT,
        text: SHARE_TEXT,
        url: shareUrlFor('web_share')
      })
      sendShareEvent('click', { share_channel: 'web_share' })
      return 'shared'
    } catch (e) {
      // ユーザーがシェアシートを閉じただけ（AbortError）はエラー扱いしない
      const name =
        e && typeof e === 'object' ? (e as { name?: string }).name : ''
      if (name !== 'AbortError') console.error(e)
      return 'cancelled'
    }
  }

  /** 端末に応じて共有 or URL コピー。共有が成立したら true */
  const shareOrCopy = async (): Promise<boolean> => {
    if (isMobileDevice.value && canWebShare.value) {
      const result = await shareViaWebShare()
      if (result === 'shared') return true
      if (result === 'cancelled') return false
    }
    return copyShareUrl()
  }

  return {
    isMobileDevice,
    canWebShare,
    lineShareUrl,
    xShareUrl,
    shareUrlFor,
    sendShareEvent,
    copyShareUrl,
    shareViaWebShare,
    shareOrCopy
  }
}
